import React, { useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

function InfiniteScrollSection() {
  const sectors = [
    {
      id: 1,
      name: "المطاعم والمقاهي",
      number: "01",
      image: "/images/مطعم.jpg"
    },
    {
      id: 2,
      name: "محطات الوقود",
      number: "02",
      image: "/images/محطة.jpg"
    },
    {
      id: 3,
      name: "الصيدليات",
      number: "03",
      image: "/images/صيدلية.jpg"
    },
    {
      id: 4,
      name: "محلات الذهب والمجوهرات",
      number: "04",
      image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&h=800&fit=crop&q=80"
    },
    {
      id: 5,
      name: "بائعين التجزئة",
      number: "05",
      image: "/images/سوبر ماركت.jpg"
    },
    {
      id: 6,
      name: "محلات الملابس",
      number: "06",
      image: "/images/محل ملابس.jpg"
    },
    {
      id: 7,
      name: "محلات الإلكترونيات",
      number: "07",
      image: "/images/متجر-الكترونياتAR08162020-1024x640.jpg"
    },
    {
      id: 8,
      name: "المشاريع الصغيرة",
      number: "08",
      image: "/images/foodtruck.png"
    },
  ];

  const autoplayRef = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      direction: 'rtl',
      align: 'start',
      slidesToScroll: 1,
      skipSnaps: false,
      dragFree: false,
    },
    [autoplayRef.current]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      autoplayRef.current.play();
    }
  }, [emblaApi]);

  return (
    <section className="relative w-full bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            مصمم من أجل{' '}
            <span className="text-primary-500">كل القطاعات</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            من المحلات الصغيرة إلى السلاسل متعددة الفروع، يتكيف جهازنا مع قطاعك
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef} dir="rtl">
            <div className="flex gap-6 lg:gap-8 pr-6 lg:pr-8 pl-6 lg:pl-8">
              {sectors.map((sector) => (
                <div
                  key={sector.id}
                  className="flex-[0_0_85%] sm:flex-[0_0_60%] md:flex-[0_0_45%] lg:flex-[0_0_23%] min-w-0"
                >
                  <div className="relative rounded-2xl overflow-hidden aspect-[3/4] group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src={sector.image}
                        alt={sector.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-800/50 to-gray-900/85"></div>
                    
                    {/* Number Badge - Primary Color Circle */}
                    <div className="absolute top-4 right-4 w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center shadow-xl z-10">
                      <span className="text-white font-bold text-base">
                        {sector.number}
                      </span>
                    </div>

                    {/* Content - Only Bottom Text */}
                    <div className="absolute inset-0 flex items-end justify-center p-6 z-10">
                      {/* Bottom Text - Large White */}
                      <div className="text-white text-2xl md:text-3xl lg:text-4xl font-bold text-center leading-tight">
                        {sector.name}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons - Flipped for RTL */}
          <button
            onClick={scrollPrev}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg hover:bg-primary-500 hover:text-white transition-all duration-300 group"
            aria-label="Previous"
          >
            <svg className="w-6 h-6 text-gray-700 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={scrollNext}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg hover:bg-primary-500 hover:text-white transition-all duration-300 group"
            aria-label="Next"
          >
            <svg className="w-6 h-6 text-gray-700 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

export default InfiniteScrollSection;
